import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Edit2, Trash2, Check, X, AlertTriangle, Loader2,
  ChevronLeft, Filter, Layers, ArrowUpDown, RefreshCw, Code
} from 'lucide-react';
import { ruleCompositionService } from '../../../../services/ruleCompositionService';
import {
  SqlFilterDto,
  SqlGroupDto,
  SqlOrderDto,
  CreateSqlFilterRequest,
  UpdateSqlFilterRequest,
  CreateSqlGroupRequest,
  UpdateSqlGroupRequest,
  CreateSqlOrderRequest,
  UpdateSqlOrderRequest,
} from '../../../../types/ruleComposition';

interface SqlSyntaxAdminProps {
  onBack?: () => void;
}

type TabType = 'filter' | 'group' | 'order';
type ModalMode = 'create' | 'edit' | null;

interface FormData {
  lng1: string;
  lng2: string;
  syntax: string;
}

const emptyFormData: FormData = {
  lng1: '',
  lng2: '',
  syntax: '',
};

export const SqlSyntaxAdmin: React.FC<SqlSyntaxAdminProps> = ({ onBack }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('filter');

  // Data state
  const [filters, setFilters] = useState<SqlFilterDto[]>([]);
  const [groups, setGroups] = useState<SqlGroupDto[]>([]);
  const [orders, setOrders] = useState<SqlOrderDto[]>([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load all data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [filtersData, groupsData, ordersData] = await Promise.all([
        ruleCompositionService.getSqlFilters(),
        ruleCompositionService.getSqlGroups(),
        ruleCompositionService.getSqlOrders(),
      ]);
      setFilters(filtersData);
      setGroups(groupsData);
      setOrders(ordersData);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke hente data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get current data based on active tab
  const getCurrentData = () => {
    const term = searchTerm.toLowerCase();
    switch (activeTab) {
      case 'filter':
        return filters.filter(
          f =>
            f.lng1.toLowerCase().includes(term) ||
            (f.lng2 && f.lng2.toLowerCase().includes(term)) ||
            f.sqlFilterSyntax.toLowerCase().includes(term)
        );
      case 'group':
        return groups.filter(
          g =>
            g.lng1.toLowerCase().includes(term) ||
            (g.lng2 && g.lng2.toLowerCase().includes(term)) ||
            g.sqlGroupSyntax.toLowerCase().includes(term)
        );
      case 'order':
        return orders.filter(
          o =>
            o.lng1.toLowerCase().includes(term) ||
            (o.lng2 && o.lng2.toLowerCase().includes(term)) ||
            o.sqlOrderSyntax.toLowerCase().includes(term)
        );
    }
  };

  // Get tab label
  const getTabLabel = (tab: TabType) => {
    switch (tab) {
      case 'filter':
        return 'SQL Filter';
      case 'group':
        return 'SQL Gruppering';
      case 'order':
        return 'SQL Sortering';
    }
  };

  // Get tab icon
  const getTabIcon = (tab: TabType) => {
    switch (tab) {
      case 'filter':
        return <Filter className="w-4 h-4" />;
      case 'group':
        return <Layers className="w-4 h-4" />;
      case 'order':
        return <ArrowUpDown className="w-4 h-4" />;
    }
  };

  // Get syntax field name
  const getSyntaxFieldName = () => {
    switch (activeTab) {
      case 'filter':
        return 'SQL Filter Syntax';
      case 'group':
        return 'SQL Group Syntax';
      case 'order':
        return 'SQL Order Syntax';
    }
  };

  // Open create modal
  const handleCreate = () => {
    setSelectedId(null);
    setFormData(emptyFormData);
    setModalMode('create');
    setFormError(null);
  };

  // Open edit modal
  const handleEdit = (id: number) => {
    setSelectedId(id);
    let data: FormData = emptyFormData;

    switch (activeTab) {
      case 'filter': {
        const filter = filters.find(f => f.sqlFilterID === id);
        if (filter) {
          data = {
            lng1: filter.lng1,
            lng2: filter.lng2 || '',
            syntax: filter.sqlFilterSyntax,
          };
        }
        break;
      }
      case 'group': {
        const group = groups.find(g => g.sqlGroupID === id);
        if (group) {
          data = {
            lng1: group.lng1,
            lng2: group.lng2 || '',
            syntax: group.sqlGroupSyntax,
          };
        }
        break;
      }
      case 'order': {
        const order = orders.find(o => o.sqlOrderID === id);
        if (order) {
          data = {
            lng1: order.lng1,
            lng2: order.lng2 || '',
            syntax: order.sqlOrderSyntax,
          };
        }
        break;
      }
    }

    setFormData(data);
    setModalMode('edit');
    setFormError(null);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.lng1.trim()) {
      setFormError('Navn (norsk) er påkrevd');
      return;
    }
    if (!formData.syntax.trim()) {
      setFormError('Syntax er påkrevd');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      switch (activeTab) {
        case 'filter': {
          if (modalMode === 'create') {
            const request: CreateSqlFilterRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlFilterSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.createSqlFilter(request);
          } else if (selectedId) {
            const request: UpdateSqlFilterRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlFilterSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.updateSqlFilter(selectedId, request);
          }
          break;
        }
        case 'group': {
          if (modalMode === 'create') {
            const request: CreateSqlGroupRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlGroupSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.createSqlGroup(request);
          } else if (selectedId) {
            const request: UpdateSqlGroupRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlGroupSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.updateSqlGroup(selectedId, request);
          }
          break;
        }
        case 'order': {
          if (modalMode === 'create') {
            const request: CreateSqlOrderRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlOrderSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.createSqlOrder(request);
          } else if (selectedId) {
            const request: UpdateSqlOrderRequest = {
              lng1: formData.lng1.trim(),
              lng2: formData.lng2.trim() || null,
              sqlOrderSyntax: formData.syntax.trim(),
            };
            await ruleCompositionService.updateSqlOrder(selectedId, request);
          }
          break;
        }
      }

      setModalMode(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Kunne ikke lagre');
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm delete
  const handleDeleteConfirm = (id: number) => {
    setIdToDelete(id);
    setShowDeleteConfirm(true);
  };

  // Execute delete
  const handleDelete = async () => {
    if (!idToDelete) return;

    setIsDeleting(true);
    try {
      switch (activeTab) {
        case 'filter':
          await ruleCompositionService.deleteSqlFilter(idToDelete);
          break;
        case 'group':
          await ruleCompositionService.deleteSqlGroup(idToDelete);
          break;
        case 'order':
          await ruleCompositionService.deleteSqlOrder(idToDelete);
          break;
      }
      setShowDeleteConfirm(false);
      setIdToDelete(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette');
    } finally {
      setIsDeleting(false);
    }
  };

  // Get ID for row
  const getRowId = (item: SqlFilterDto | SqlGroupDto | SqlOrderDto) => {
    if ('sqlFilterID' in item) return item.sqlFilterID;
    if ('sqlGroupID' in item) return item.sqlGroupID;
    if ('sqlOrderID' in item) return item.sqlOrderID;
    return 0;
  };

  // Get syntax for row
  const getRowSyntax = (item: SqlFilterDto | SqlGroupDto | SqlOrderDto) => {
    if ('sqlFilterSyntax' in item) return item.sqlFilterSyntax;
    if ('sqlGroupSyntax' in item) return item.sqlGroupSyntax;
    if ('sqlOrderSyntax' in item) return item.sqlOrderSyntax;
    return '';
  };

  const currentData = getCurrentData();

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Tilbake"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <Code className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">SQL Syntaks Administrasjon</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Oppdater"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Ny</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {(['filter', 'group', 'order'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {getTabIcon(tab)}
              <span>{getTabLabel(tab)}</span>
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-200 rounded-full">
                {tab === 'filter' ? filters.length : tab === 'group' ? groups.length : orders.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Sok etter navn eller syntax..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Feil ved lasting av data</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={loadData}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
              >
                Prov igjen
              </button>
            </div>
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-12">
            <Code className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Ingen treff' : `Ingen ${getTabLabel(activeTab).toLowerCase()} funnet`}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm
                ? 'Prov et annet sokord'
                : `Klikk "Ny" for a opprette en ${getTabLabel(activeTab).toLowerCase()}`}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Navn (NO)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Navn (EN)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Syntax
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.map(item => (
                  <tr key={getRowId(item)} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">{getRowId(item)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.lng1}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.lng2 || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {getRowSyntax(item).length > 60
                          ? getRowSyntax(item).substring(0, 60) + '...'
                          : getRowSyntax(item)}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(getRowId(item))}
                          className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                          title="Rediger"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteConfirm(getRowId(item))}
                          className="p-1.5 hover:bg-red-100 rounded text-red-600 transition-colors"
                          title="Slett"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' ? `Ny ${getTabLabel(activeTab)}` : `Rediger ${getTabLabel(activeTab)}`}
              </h2>
              <button
                onClick={() => setModalMode(null)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-700">{formError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Navn (norsk) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lng1}
                  onChange={e => setFormData({ ...formData, lng1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="F.eks. Filtrer pa aktive ordrer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Navn (engelsk)</label>
                <input
                  type="text"
                  value={formData.lng2}
                  onChange={e => setFormData({ ...formData, lng2: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="F.eks. Filter active orders"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {getSyntaxFieldName()} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.syntax}
                  onChange={e => setFormData({ ...formData, syntax: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder={
                    activeTab === 'filter'
                      ? 'F.eks. OrderStatus > 0 AND OrderStatus < 100'
                      : activeTab === 'group'
                      ? 'F.eks. CustomerNo'
                      : 'F.eks. ORDER BY DeliveryDate ASC'
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  {activeTab === 'filter' && 'SQL WHERE-betingelse (uten WHERE-nokkelen)'}
                  {activeTab === 'group' && 'Kolonner a gruppere pa (kommaseparert)'}
                  {activeTab === 'order' && 'SQL ORDER BY-uttrykk (med ORDER BY-nokkelen)'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setModalMode(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isSaving}
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Lagrer...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Lagre</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Bekreft sletting</h2>
              </div>
              <p className="text-gray-600">
                Er du sikker pa at du vil slette denne {getTabLabel(activeTab).toLowerCase()}en? Denne handlingen kan
                ikke angres.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setIdToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sletter...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Slett</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SqlSyntaxAdmin;
